<template>
  <div class="eod-summary">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
      <div class="eod-heading title-page mb-0">
        <i class="material-icons-round eod-heading-icon">receipt_long</i>
        <span>{{ $t("eodTitle") }}</span>
      </div>
      <div class="d-flex align-items-center gap-2">
        <div class="eod-note">{{ $t("eodNote") }}</div>
        <button
          type="button"
          class="eod-icon-btn"
          :title="$t('dashCustomize')"
          @click="$emit('customize')"
        >
          <i class="material-icons-round">settings</i>
        </button>
      </div>
    </div>
    <div v-if="loading" class="text-center py-4">{{ $t("eodLoading") }}</div>
    <div v-else-if="!data" class="text-center py-4">{{ $t("eodNoData") }}</div>
    <div v-else-if="allHidden" class="eod-empty text-center py-4">{{ $t("eodAllHidden") }}</div>
    <div v-else class="row mt-1">
      <div v-if="show('operationSummary') || show('orderSource')" class="col-lg-4 col-12 eod-col">
        <div class="eod-box">
          <template v-if="show('operationSummary')">
          <div class="eod-title">{{ $t("eodOperationSummary") }}</div>
          <div class="eod-row">
            <span>{{ $t("eodReceiptCount") }}</span><span>{{ data.operation.receipt_count }}</span>
          </div>
          <div v-if="data.operation.receipt_from" class="eod-sub text-end">
            {{ data.operation.receipt_from }} - {{ data.operation.receipt_to }}
          </div>
          <div class="eod-row">
            <span>{{ $t("eodVoidCount") }}</span>
            <span>{{ data.operation.void_bill_count }} / {{ data.operation.void_item_qty }}</span>
          </div>
          <div class="eod-row"><span>{{ $t("eodGuest") }}</span><span>{{ data.operation.guest }}</span></div>
          <div class="eod-row"><span>{{ $t("eodGrossSales") }}</span><span>{{ money(data.operation.gross_sales) }}</span></div>
          <div class="eod-row"><span>{{ $t("eodDiscount") }}</span><span>{{ money(data.operation.discount) }}</span></div>
          <div class="eod-row"><span>{{ $t("eodNetSales") }}</span><span>{{ money(data.operation.net_sales) }}</span></div>
          <div class="eod-row"><span>{{ $t("eodServiceCharge") }}</span><span>{{ money(data.operation.service_charge) }}</span></div>
          <div class="eod-row"><span>{{ $t("eodGst") }}</span><span>{{ money(data.operation.gst) }}</span></div>
          <div class="eod-row"><span>{{ $t("eodRounding") }}</span><span>{{ money(data.operation.rounding) }}</span></div>
          <div class="eod-row eod-total"><span>{{ $t("eodTotalSales") }}</span><span>{{ money(data.operation.total_sales) }}</span></div>
          <div class="eod-row"><span>{{ $t("eodBcrs") }}</span><span>{{ money(data.operation.bcrs) }}</span></div>
          <div class="eod-row"><span>{{ $t("eodVoidAmount") }}</span><span>{{ money(data.operation.void_amount) }}</span></div>
          </template>

          <template v-if="show('orderSource')">
          <div class="eod-title" :class="{ 'mt-3': show('operationSummary') }">{{ $t("eodOrderSource") }}</div>
          <div v-if="!data.channels.length" class="eod-empty">{{ $t("eodNoSales") }}</div>
          <div v-for="channel in data.channels" :key="channel.platform_type" class="eod-row">
            <span>{{ channel.label }} · {{ $t("eodBills", { count: channel.bill_count }) }}</span><span>{{ money(channel.total) }}</span>
          </div>
          </template>
        </div>
      </div>

      <div v-if="show('discount') || show('paymentSummary')" class="col-lg-4 col-12 eod-col">
        <div class="eod-box">
          <template v-if="show('discount')">
          <div class="eod-title">{{ $t("eodDiscount") }}</div>
          <div class="eod-row">
            <span>{{ $t("eodVoucher") }} ({{ data.discounts.voucher.count }})</span>
            <span>-{{ money(data.discounts.voucher.amount) }}</span>
          </div>
          <div class="eod-row">
            <span>{{ $t("eodPromoCode") }} ({{ data.discounts.promo_code.count }})</span>
            <span>-{{ money(data.discounts.promo_code.amount) }}</span>
          </div>
          <div class="eod-row">
            <span>{{ $t("eodBillDiscount") }} ({{ data.discounts.bill_discount.count }})</span>
            <span>-{{ money(data.discounts.bill_discount.amount) }}</span>
          </div>
          <div v-for="row in data.discounts.bill_discount.by_label" :key="row.label" class="eod-row eod-indent">
            <span>{{ row.label }} ({{ row.count }})</span><span>{{ money(row.amount) }}</span>
          </div>
          <div class="eod-row"><span>{{ $t("eodRedemption") }}</span><span>-{{ money(data.discounts.redemption) }}</span></div>
          </template>

          <template v-if="show('paymentSummary')">
          <div class="eod-title" :class="{ 'mt-3': show('discount') }">{{ $t("eodPaymentSummary") }}</div>
          <div v-if="!data.payments.rows.length" class="eod-empty">{{ $t("eodNoPayment") }}</div>
          <div class="eod-scroll eod-scroll-sm">
            <div v-for="row in data.payments.rows" :key="row.method" class="eod-row">
              <span>{{ row.method }} ({{ row.count }})</span><span>{{ money(row.amount) }}</span>
            </div>
          </div>
          <div class="eod-row eod-total"><span>{{ $t("eodTotalPayments") }}</span><span>{{ money(data.payments.total) }}</span></div>
          <div v-if="data.payments.zero_methods.length" class="eod-sub mt-2">
            {{ $t("eodZeroPayments") }}: {{ data.payments.zero_methods.join(', ') }}
          </div>
          </template>
        </div>
      </div>

      <div v-if="show('topup') || show('cashback')" class="col-lg-4 col-12 eod-col">
        <div class="eod-box">
          <template v-if="show('topup')">
          <div class="eod-title">{{ $t("eodTopup") }}</div>
          <div v-if="!data.topup.by_method.length" class="eod-empty">{{ $t("eodNoTopup") }}</div>
          <div v-for="row in data.topup.by_method" :key="row.method" class="eod-row">
            <span>{{ row.method }} ({{ row.count }})</span><span>{{ money(row.amount) }}</span>
          </div>
          <div class="eod-row eod-total"><span>{{ $t("eodTotal") }}</span><span>{{ money(data.topup.amount) }}</span></div>
          <div class="eod-row"><span>{{ $t("eodBonus") }}</span><span>{{ money(data.topup.bonus) }}</span></div>
          </template>

          <template v-if="show('cashback')">
          <div class="eod-title" :class="{ 'mt-3': show('topup') }">{{ $t("eodCashbackSummary") }}</div>
          <div class="eod-row"><span>{{ $t("eodMemberBills") }}</span><span>{{ data.cashback.member_bill_count }}</span></div>
          <div class="eod-row"><span>{{ $t("eodCashbackIssued") }}</span><span>{{ money(data.cashback.issued) }}</span></div>
          <div class="eod-row"><span>{{ $t("eodCashbackUsed") }}</span><span>{{ money(data.cashback.cashback_used) }}</span></div>
          <div class="eod-row"><span>{{ $t("eodTopupUsed") }}</span><span>{{ money(data.cashback.topup_used) }}</span></div>
          <div class="eod-row"><span>{{ $t("eodRedemption") }}</span><span>{{ money(data.cashback.redemption) }}</span></div>
          </template>
        </div>
      </div>

      <div v-if="show('groupSummary')" :class="show('addOnSummary') ? 'col-lg-8' : 'col-lg-12'" class="col-12 eod-col">
        <div class="eod-box">
          <div class="d-flex justify-content-between align-items-center eod-title">
            <span>{{ $t("eodGroupSummary") }}</span>
            <span>{{ $t("eodTotalQtySold") }}: {{ qty(data.items.total_qty) }}</span>
          </div>
          <div v-if="!data.items.types.length" class="eod-empty">{{ $t("eodNoItem") }}</div>
          <div class="eod-scroll">
          <div v-for="type in data.items.types" :key="type.type_name" class="eod-type">
            <div v-for="group in type.groups" :key="group.group_name">
              <div class="eod-row eod-group cursor-pointer" @click="toggle(type.type_name + '|' + group.group_name)">
                <span>
                  <i class="material-icons-round eod-caret">
                    {{ isOpen(type.type_name + '|' + group.group_name) ? 'expand_more' : 'chevron_right' }}
                  </i>
                  {{ group.group_name }} ({{ qty(group.qty) }})
                </span>
                <span>{{ money(group.amount) }}</span>
              </div>
              <template v-if="isOpen(type.type_name + '|' + group.group_name)">
                <div v-for="item in group.items" :key="item.item_name" class="eod-row eod-indent">
                  <span>{{ item.item_name }} ({{ qty(item.qty) }})</span><span>{{ money(item.amount) }}</span>
                </div>
              </template>
            </div>
            <div class="eod-row eod-total">
              <span>{{ $t("eodTypeTotal", { name: type.type_name }) }} ({{ qty(type.qty) }})</span><span>{{ money(type.amount) }}</span>
            </div>
          </div>
          </div>
          <div class="eod-sub mt-2">{{ $t("eodItemNote") }}</div>
        </div>
      </div>

      <div v-if="show('addOnSummary')" :class="show('groupSummary') ? 'col-lg-4' : 'col-lg-12'" class="col-12 eod-col">
        <div class="eod-box">
          <div class="d-flex justify-content-between align-items-center eod-title">
            <span>{{ $t("eodAddOnSummary") }}</span>
            <span>{{ $t("eodQty") }}: {{ qty(data.add_ons.total_qty) }}</span>
          </div>
          <div v-if="!data.add_ons.rows.length" class="eod-empty">{{ $t("eodNoAddOn") }}</div>
          <div class="eod-scroll">
            <div v-for="row in data.add_ons.rows" :key="row.name" class="eod-row">
              <span>{{ row.name }} ({{ qty(row.qty) }})</span><span>{{ money(row.amount) }}</span>
            </div>
          </div>
          <div class="eod-sub mt-2">{{ $t("eodAddOnNote") }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DashboardService from "../../../services/dashboard.service";

const SECTIONS = ["operationSummary", "orderSource", "discount", "paymentSummary", "topup", "cashback", "groupSummary", "addOnSummary"];

export default {
  name: "EodSummary",
  props: {
    physicalBranchId: { type: Number, default: 0 },
    timeType: { type: Number, default: 1 },
    // dashboard_config_eod: section key -> shown. A section never saved is shown.
    config: { type: Object, default: null },
  },
  emits: ["customize"],
  data() {
    return {
      loading: false,
      data: null,
      openGroups: {},
      requestId: 0,
    };
  },
  created() {
    this.load();
  },
  watch: {
    physicalBranchId() {
      this.load();
    },
    timeType() {
      this.load();
    },
    // Sections hidden by the merchant are not read by the API, so a section turned back on
    // needs a fresh load.
    config() {
      this.load();
    },
  },
  computed: {
    allHidden() {
      return SECTIONS.every((section) => !this.show(section));
    },
  },
  methods: {
    load() {
      // Only the latest filter's answer is kept when the filters change quickly.
      const requestId = ++this.requestId;
      this.loading = true;

      DashboardService.eodSummary({
        physical_branch_id: this.physicalBranchId,
        time_type: this.timeType,
      })
        .then((response) => {
          if (requestId !== this.requestId) {
            return;
          }
          this.data = response.data.result.isSuccess ? response.data.data : null;
        })
        .catch(() => {
          if (requestId === this.requestId) {
            this.data = null;
          }
        })
        .finally(() => {
          if (requestId === this.requestId) {
            this.loading = false;
          }
        });
    },
    money(value) {
      return Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    qty(value) {
      return Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
    },
    show(section) {
      return !this.config || this.config[section] !== false;
    },
    toggle(key) {
      this.openGroups = { ...this.openGroups, [key]: !this.openGroups[key] };
    },
    isOpen(key) {
      return !!this.openGroups[key];
    },
  },
};
</script>

<style scoped>
.eod-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.eod-heading-icon {
  display: flex;
  align-items: center;
  color: #133067;
  font-size: 22px !important;
}

.eod-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #7b809a;
  cursor: pointer;
}

.eod-icon-btn:hover {
  color: #000;
}

.eod-note {
  font-size: 12.5px;
}

.eod-col {
  margin-top: 15px;
}

.eod-box {
  background: #ffffff;
  border-radius: 15px;
  padding: 16px 20px;
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
  height: 100%;
}

.eod-title {
  font-size: 18px;
  color: #133067;
  border-bottom: 1px solid #133067;
  margin-bottom: 8px;
  padding-bottom: 4px;
  font-weight: 500;
}

.eod-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  padding: 3px 0;
  color: #344767;
}

.eod-row > span:last-child {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.eod-indent {
  padding-left: 22px;
}

.eod-total {
  border-top: 1px dashed #c4c4c4;
  font-weight: bold;
  color: #133067;
}

.eod-group {
  font-weight: 500;
}

.eod-caret {
  font-size: 18px !important;
  vertical-align: middle;
}

.eod-type + .eod-type {
  margin-top: 10px;
}

.eod-sub {
  font-size: 12.5px;
}

.eod-empty {
  font-size: 14px;
  padding: 3px 0;
}

.eod-scroll {
  max-height: 420px;
  overflow-y: auto;
}

.eod-scroll-sm {
  max-height: 260px;
}
</style>
